import React, { useState, useCallback, useRef, useEffect, useMemo, Activity } from "react";
import { Outlet, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import toast from "react-hot-toast";
import ProjectBanner from "../project-banner/ProjectBanner";
import ProjectAmbientBackground from "./ProjectAmbientBackground";
import BottomAppBarProject from "../bottom-app-bar/BottomAppBarProject";
import PrimaryActionHandler, {
  type PrimaryActionHandlerRef,
} from "../bottom-app-bar/PrimaryActionHandler";
import {
  PrimaryActionContext,
  ProjectPrimaryActionBar,
  useProjectPrimaryAction,
} from "../bottom-app-bar/ProjectPrimaryAction";
import ErrorBoundary from "../global/ErrorBoundary";
import SvgLoadingAnimation from "../loading";
import Home from "../../pages/home/Home";
import TalkRoom from "../../pages/talkroom/TalkRoom";
import DM from "../../pages/dm/DM";
import ProjectConfig from "../../pages/project-settings/ProjectConfig";
import Notes from "../../pages/notes/Notes";
import Library from "../../pages/library/Library";
import { FloatingProjectMenu, ProjectMenu } from "./ProjectMenu";
import { ProjectShellContext } from "./ProjectShellContext";
import useProjectShortcuts from "./useProjectShortcuts";
import { useGetCurrentUserQuery } from "../../app/accounts/authuser";
import { useGetProjectMembershipQuery } from "../../app/accounts/memberships";
import { useGetProjectQuery } from "../../app/services/projects";
import { useGetNotesTreeQuery } from "../../app/services/notes";
import { useGetLibraryTreeQuery } from "../../app/services/library";
import {
  projectMainBoardCacheKey,
  useEnsureProjectMainBoardMutation,
} from "../../app/services/imageBoard";
import {
  isPrimaryClientPosition,
  isProducerGroupPosition,
  isSpecialistPosition,
} from "../../utils/memberPositionUtils";
import { getProjectAmbient } from "../../utils/projectAmbient";
import { BOARD_LINK_PARAMS } from "../../utils/deepLink";

/**
 * isolation: isolate で独立した重ね合わせ文脈を作り、z-index: -1 の
 * アンビエント背景（ProjectAmbientBackground）が「このラッパーの背面かつ
 * ページ全体の背面には抜けない」位置に収まるようにする。
 * ルート要素の style でプロジェクトのバナー色由来の --soremo-background を
 * 差し替えるため、配下の var(--soremo-background) 参照は一括で薄く色づく。
 *
 * min-height はグローバルヘッダーの表示状態と連動させる。親（GlobalWrapper の
 * ContentWrapper）がヘッダー表示中は padding-top: 64px を足すため、ここを
 * 100dvh 固定にするとドキュメント全体が 64px はみ出し、ヘッダーの出し入れの
 * たびにウィンドウのスクロールバーが出たり消えたりして画面全体が横にズレる。
 * padding-top と合算して常にちょうど 100dvh になるよう差し引き、transition も
 * ContentWrapper / OutletContainer と同じ 0.15s linear で揃えて、アニメーション
 * 中も合計が一定になるようにする。
 */
const ProjectWrapperStyle = styled.div<{ $scrollState: string }>`
  min-height: ${({ $scrollState }) =>
    $scrollState === "down"
      ? "100dvh"
      : "calc(100dvh - var(--global-header-height))"};
  transition: min-height 0.15s linear;
  position: relative;
  isolation: isolate;
`;

// Outlet用のコンテナ - スクロール状態に応じて動的に高さを計算
const OutletContainer = styled.div<{ $scrollState: string }>`
  display: flex;
  flex-direction: column;
  position: relative;

  /*
   * 高さ計算:
   * - scrollState === "up": GlobalHeader(64px) + ProjectBanner(75px) を引く
   * - scrollState === "down": ProjectBanner(75px) のみ引く（GlobalHeaderは非表示）
   *
   * BottomAppBar(80px) は差し引かない。バーは透明でコンテンツはその背後を
   * 通ってビューポート下端まで描画される（各ページのスクロール層が
   * 下端 padding で BottomAppBar + 入力欄との重なりを回避する）。
   */
  height: ${({ $scrollState }) =>
    $scrollState === "down"
      ? "calc(100dvh - var(--project-banner-height))"
      : "calc(100dvh - var(--global-header-height) - var(--project-banner-height))"};

  transition: height 0.15s linear;
`;

/**
 * ノート / ライブラリの全画面パネル。
 * ベースタブを覆うスタイルのため、専用のアイコン行は設けず、コンテンツを
 * 上端まで広げた上にアイコン（プロジェクトメニュー）だけを FloatingProjectMenu で重ねる。
 * ノート / ライブラリは下部バーのタブなので、他のタブと同じく即時に切り替える。
 * $fill はパネルの高さを OutletContainer いっぱいに確定させ、コンテンツを
 * パネル内部でスクロールさせるためのフラグ（全パネル共通。
 * ノートは左サイドバーと本文を独立スクロールさせるため高さの確定が必要）。
 */
const PanelTab = styled.div<{ $fill?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  /* ベースタブのコンテンツより上、固定 UI（BottomAppBar: z4 /
     ProjectBanner とそのユーザーリスト: z5）より下。5 にすると
     コンテンツが伸びたときに下部ナビやバナーのポップオーバーを
     覆い隠してしまう。 */
  z-index: 3;
  /* 不透明は維持しつつ、バナー色由来の tint（ProjectWrapper で差し替えた
     --soremo-background）を継承する。isolation はパネル内に敷く
     container 版アンビエント背景（z-index: -1）を「パネルの背景より上・
     コンテンツより下」に収めるための重ね合わせ文脈。 */
  background: var(--soremo-background);
  isolation: isolate;
  ${({ $fill }) => ($fill ? "flex: 1; min-height: 0;" : "")}
`;

// Activity（visitedTabs）ではなくパネル（PanelTab）で描画するタブ。
// ノート / ライブラリは下部バーのタブ。imageboard は旧 URL（?tab=imageboard）を
// ノートタブのメインのボードへ振り替えるまでの待機表示だけを出す。
const PANEL_TABS = ["imageboard", "notes", "library"];

// ディープリンク対応: 有効なタブ名の定義
const VALID_TABS = ["home", "talkroom", "dm", "config", ...PANEL_TABS];

const isValidTab = (tab: string | null): tab is string =>
  tab !== null && VALID_TABS.includes(tab);

const ProjectWrapper: React.FC = () => {
  const scrollState = useSelector((state: { layout: string }) => state.layout);
  const { projectId, shotId } = useParams<{ projectId: string; shotId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const primaryActionRef = useRef<PrimaryActionHandlerRef>(null);

  // メンバーシップポジションを取得（SPECIALIST 判定用）
  const { data: currentUserData } = useGetCurrentUserQuery();
  const {
    data: membershipData,
    isSuccess: membershipLoaded,
    isError: membershipFailed,
  } = useGetProjectMembershipQuery(projectId ?? "", {
    skip: !projectId || !currentUserData,
  });
  const positionStr = membershipData?.data?.position ?? undefined;
  const isSpecialist = positionStr ? isSpecialistPosition(positionStr) : false;
  // イメージボード（プロジェクトのメインのボード）を開けるのは、成員のうち
  // スペシャリスト以外。成員でない（管理者の閲覧等）ことが確定したら開けない
  const imageBoardDenied =
    isSpecialist || ((membershipLoaded || membershipFailed) && !positionStr);
  // プロジェクトの設定（config タブ）はプロデューサーグループとクライアント責任者のみ
  const canOpenSettings = positionStr
    ? isProducerGroupPosition(positionStr) || isPrimaryClientPosition(positionStr)
    : false;

  // 納品物なし（チェックポイント0）はホームを使わずトークルーム中心。
  const { data: projectData } = useGetProjectQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId || !currentUserData },
  );
  const projectLoaded = !!projectData?.data?.project;
  const noDeliverable = projectData?.data?.project?.total_shots_count === 0;

  // バナー色由来のアンビエント背景（未取得時は banner_color=1 相当に
  // フォールバック。ProjectBanner のデフォルトと同じ）
  const ambient = getProjectAmbient(projectData?.data?.project?.banner_color);

  // ノート / ライブラリのディープリンク権限判定用（下部バー・パネル
  // 本体と同一クエリのためキャッシュを共有し、追加リクエストは発生しない）。
  // BE はアクセス可能なセクションのみ返すため、「取得完了かつセクション 0 件
  // かつ管理権限なし」をアクセス権なしとみなす。取得完了までは判定しない
  // （undefined の間はタブを開き、ページ側のローディング表示に任せる）。
  const { data: notesTreeData } = useGetNotesTreeQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId || !currentUserData },
  );
  const { data: libraryTreeData } = useGetLibraryTreeQuery(
    { project_id: projectId ?? "" },
    { skip: !projectId || !currentUserData },
  );
  const notesAccessDenied =
    !!notesTreeData &&
    (notesTreeData.data?.sections ?? []).length === 0 &&
    !(notesTreeData.data?.can_manage ?? false);
  const libraryAccessDenied =
    !!libraryTreeData &&
    (libraryTreeData.data?.sections ?? []).length === 0 &&
    !(libraryTreeData.data?.can_manage ?? false);

  // Preview ページかどうかを判定
  const isPreviewRoute = !!shotId;

  // クエリパラメータからタブの初期値を決定（バリデーション付き）
  const [initialTab] = useState(() => {
    const tabParam = searchParams.get("tab");
    return isValidTab(tabParam) ? tabParam : "home";
  });

  // 訪問済みタブを管理（Activity の遅延マウント用）。パネルタブは
  // Activity ではなくパネルで描画するため含めない。
  const [visitedTabs, setVisitedTabs] = useState<Set<string>>(
    () => new Set(PANEL_TABS.includes(initialTab) ? [] : [initialTab]),
  );
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  // タブ変更時に訪問済みに追加 & URLのクエリパラメータも同期
  const handleTabChange = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      setVisitedTabs((prev) => new Set([...prev, tab]));
      // URLにタブ情報を反映（homeの場合はクエリパラメータを削除）
      setSearchParams(tab === "home" ? {} : { tab }, { replace: true });
    },
    [setSearchParams],
  );

  // プロジェクト内キーボードショートカット（⌥3〜⌥9 / Esc。権限判定込み）
  useProjectShortcuts({ onTabChange: handleTabChange, isPreviewRoute, activeTab });

  // プロジェクトの設定（config タブ）を開く（右上のプロジェクトメニュー /
  // バナーのコードネーム）。Preview 表示中はタブが描画されないため、Preview を
  // 閉じて開く。開く前のタブを控え、コードネームの再クリックで戻れるようにする。
  const tabBeforeSettingsRef = useRef<string | null>(null);
  const isSettingsOpen = !isPreviewRoute && activeTab === "config";
  const openSettings = useCallback(() => {
    if (isPreviewRoute) {
      tabBeforeSettingsRef.current = null;
      navigate(`/${projectId}?tab=config`);
      return;
    }
    if (activeTab === "config") return;
    tabBeforeSettingsRef.current = activeTab;
    handleTabChange("config");
  }, [isPreviewRoute, navigate, projectId, activeTab, handleTabChange]);
  // コードネーム用: 設定を開いているときは開く前のタブへ戻す。直リンク等で
  // 戻り先が無い場合はホーム（納品物なしはトークルーム）へ戻す。
  const toggleSettings = useCallback(() => {
    if (!isSettingsOpen) {
      openSettings();
      return;
    }
    const fallback = noDeliverable ? "talkroom" : "home";
    handleTabChange(tabBeforeSettingsRef.current ?? fallback);
    tabBeforeSettingsRef.current = null;
  }, [isSettingsOpen, openSettings, noDeliverable, handleTabChange]);
  const shellValue = useMemo(
    () => ({ canOpenSettings, isSettingsOpen, openSettings, toggleSettings }),
    [canOpenSettings, isSettingsOpen, openSettings, toggleSettings],
  );

  // 主アクション（提出 / 資料を送る）。コメントフィールド等の slot が
  // 無いときだけ BottomAppBar の上の行に出す。
  const triggerPrimaryAction = useCallback(() => {
    primaryActionRef.current?.trigger();
  }, []);
  const { value: primaryActionValue, showBar: showPrimaryActionBar } =
    useProjectPrimaryAction({
      projectId,
      activeTab,
      isPreviewRoute,
      onTrigger: triggerPrimaryAction,
    });

  // 既にプロジェクト画面に居る状態で、外部（例: グローバルメニューの
  // 進行中モーダル）から `?tab=dm&thread=...` でディープリンクされた場合、
  // ルートが同じ `/:projectId` のため `ProjectWrapper` は再マウントされない。
  // `initialTab` は useState 初期化子でしか読まれないため、`activeTab` を
  // クエリ変更へリアクティブに追従させる effect を別途用意する。
  // activeTab を動かすのは `?tab=` 自体が変わったときだけにする。
  // handleTabChange は activeTab を即時に、URL を遷移（遅れて反映）で更新するため、
  // その間に activeTab の変化だけで走ると古い `?tab=` で元のタブへ戻してしまう
  // （下部バーを 2 回押さないと切り替わらない不具合）。
  // - `null`（削除されたケース）: パネルタブ（notes/library）表示中に限り
  //   home へ戻す。
  // - 値が VALID_TABS に含まれ、現在の `activeTab` と異なるときだけ更新する。
  // - アクセス権限のないタブへのディープリンクは黙って無視せず、トーストで
  //   伝えたうえで `?tab=` と付随のディープリンクパラメータを取り除く
  //   （権限データの取得完了を待つため、こちらは URL が変わらなくても判定する）。
  const deniedToastTabRef = useRef<string | null>(null);
  const lastTabParamRef = useRef(searchParams.get("tab"));
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const tabParamChanged = tabParam !== lastTabParamRef.current;
    lastTabParamRef.current = tabParam;
    if (!tabParam) {
      if (tabParamChanged && PANEL_TABS.includes(activeTab)) {
        setActiveTab("home");
        setVisitedTabs((prev) => new Set([...prev, "home"]));
      }
      return;
    }
    if (!isValidTab(tabParam)) return;
    // 権限なし: イメージボードはスペシャリストと非成員、ノート / ライブラリは
    // 閲覧できるセクションが 1 つも無い場合（取得完了後に判定）。
    const denied =
      (tabParam === "imageboard" && imageBoardDenied) ||
      (tabParam === "notes" && notesAccessDenied) ||
      (tabParam === "library" && libraryAccessDenied);
    if (denied) {
      // StrictMode の二重実行やパラメータ除去までの再レンダーで
      // トーストが重複しないよう、通知済みタブを控える
      if (deniedToastTabRef.current !== tabParam) {
        deniedToastTabRef.current = tabParam;
        toast.error("アクセス権限がありません");
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          // 各タブのディープリンクパラメータも残さない
          [...BOARD_LINK_PARAMS, "note", "card", "section", "folder", "file"].forEach(
            (key) => next.delete(key),
          );
          return next;
        },
        { replace: true },
      );
      return;
    }
    if (!tabParamChanged || tabParam === activeTab) return;
    setActiveTab(tabParam);
    if (!PANEL_TABS.includes(tabParam)) {
      setVisitedTabs((prev) => new Set([...prev, tabParam]));
    }
  }, [
    searchParams,
    activeTab,
    imageBoardDenied,
    notesAccessDenied,
    libraryAccessDenied,
    setSearchParams,
  ]);

  // 旧イメージボード URL（?tab=imageboard[&item=…|&bx=…]）の振り替え。
  // イメージボードは共有ノートのページになったため、プロジェクトのメインの
  // ボード（無ければ BE が作る）をノートタブで開く。ボード内の位置の
  // パラメータはそのまま引き継ぎ、ImageBoard が復元する。
  const [ensureMainBoard] = useEnsureProjectMainBoardMutation({
    fixedCacheKey: projectMainBoardCacheKey(projectId),
  });
  const redirectingBoardRef = useRef(false);
  useEffect(() => {
    if (searchParams.get("tab") !== "imageboard") return;
    if (!projectId || !positionStr || isSpecialist) return;
    if (redirectingBoardRef.current) return;
    redirectingBoardRef.current = true;
    ensureMainBoard({ project_id: projectId })
      .unwrap()
      .then((res) => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams();
            next.set("tab", "notes");
            next.set("note", res.data.note_id);
            BOARD_LINK_PARAMS.forEach((key) => {
              const value = prev.get(key);
              if (value !== null) next.set(key, value);
            });
            return next;
          },
          { replace: true },
        );
      })
      .catch(() => {
        toast.error("イメージボードを開けませんでした");
        setSearchParams({}, { replace: true });
      })
      .finally(() => {
        redirectingBoardRef.current = false;
      });
  }, [searchParams, projectId, positionStr, isSpecialist, ensureMainBoard, setSearchParams]);

  // 初期表示タブの補正。`?thread=` 等のディープリンクを壊さないよう
  // searchParams は書き換えず activeTab のみ変更する。
  // - SPECIALIST: アクセス可能なのが DM のみのため DM に切り替える。
  // - 納品物なし: ホームが無いため、ホーム表示時はトークルームに切り替える。
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultRef.current) return;
    if (!positionStr || !projectLoaded) return;
    appliedDefaultRef.current = true;
    if (isSpecialist) {
      if (activeTab !== "dm") {
        setActiveTab("dm");
        setVisitedTabs((prev) => new Set([...prev, "dm"]));
      }
    } else if (noDeliverable && activeTab === "home") {
      setActiveTab("talkroom");
      setVisitedTabs((prev) => new Set([...prev, "talkroom"]));
    }
  }, [positionStr, projectLoaded, isSpecialist, noDeliverable, activeTab]);

  // Activity の mode を決定（Preview 時は全て hidden）
  const getActivityMode = useCallback(
    (tab: string): "visible" | "hidden" => {
      if (isPreviewRoute) return "hidden";
      return activeTab === tab ? "visible" : "hidden";
    },
    [isPreviewRoute, activeTab],
  );

  return (
    <ProjectShellContext.Provider value={shellValue}>
    <PrimaryActionContext.Provider value={primaryActionValue}>
    <ProjectWrapperStyle
      $scrollState={scrollState}
      style={{ "--soremo-background": ambient.background } as React.CSSProperties}
    >
      {/* バナー色に呼応してゆっくり漂う白基調のアンビエント背景 */}
      <ProjectAmbientBackground accents={ambient.accents} />
      {/* トースト表示は App.tsx の SoremoToaster に統合。位置 / 見た目を統一する
        * ため、プロジェクト画面個別の Toaster は廃止した。 */}
      {/* プロジェクト内で共通して使う部分 */}
      <ProjectBanner />

      {/* ページごとのコンテンツを表示 - OutletContainerで高さを制御 */}
      <OutletContainer $scrollState={scrollState}>
        <ErrorBoundary>
          {/* Preview ページの場合は Outlet で表示 */}
          {isPreviewRoute && <Outlet />}

          {/* Activity で管理するページ（遅延初期化パターン） */}
          {visitedTabs.has("home") && (
            <Activity mode={getActivityMode("home")}>
              {/* isActiveTab: ホーム内の InProgressWheel が下部パネルを
                  document.body へ Portal しており Activity の display:none が
                  効かないため、他タブ（ノート含む）や Preview 表示中は
                  パネルを描画しないよう明示的に伝える */}
              <Home isActiveTab={getActivityMode("home") === "visible"} />
            </Activity>
          )}
          {visitedTabs.has("talkroom") && (
            <Activity mode={getActivityMode("talkroom")}>
              <TalkRoom />
            </Activity>
          )}
          {visitedTabs.has("dm") && (
            <Activity mode={getActivityMode("dm")}>
              {/* DM は `selectedThreadId` 等の内部 state と URL クエリ (`?thread=`) の
                  両方向同期で動く。ルートが同じ `/:projectId` のため別プロジェクトに
                  navigate しても DM 自体は再マウントされず、前プロジェクトの選択状態
                  と新 URL の `?thread=` の競合が稀にタイミング次第で取りこぼし
                  （1度目で選択されず2度目で反映）を起こす。`projectId` を key にして
                  プロジェクト切替時に DM を強制再マウントし、内部 state を確実に
                  初期化することで初回ナビゲーションでも確実に反映されるようにする。 */}
              <DM key={projectId} />
            </Activity>
          )}
          {visitedTabs.has("config") && (
            <Activity mode={getActivityMode("config")}>
              <ProjectConfig />
            </Activity>
          )}
          {/* 旧イメージボード URL の振り替え待ち（上の effect がノートタブへ移す） */}
          {!isPreviewRoute && activeTab === "imageboard" && (
            <PanelTab key="imageboard-redirect" $fill>
              <SvgLoadingAnimation />
            </PanelTab>
          )}
          {/* プロジェクト絞り込みノート（下部バーのノートタブ。イメージボードもここ） */}
          {!isPreviewRoute && activeTab === "notes" && (
            <PanelTab key="notes-panel" $fill>
              <ProjectAmbientBackground variant="container" accents={ambient.accents} />
              <FloatingProjectMenu>
                <ProjectMenu />
              </FloatingProjectMenu>
              <Notes embedded />
            </PanelTab>
          )}
          {/* プロジェクト絞り込み共有ライブラリ（下部バーのライブラリタブ） */}
          {!isPreviewRoute && activeTab === "library" && (
            <PanelTab key="library-panel" $fill>
              <ProjectAmbientBackground variant="container" accents={ambient.accents} />
              <FloatingProjectMenu>
                <ProjectMenu />
              </FloatingProjectMenu>
              <Library embedded />
            </PanelTab>
          )}
        </ErrorBoundary>
      </OutletContainer>

      {/* 主アクション（提出 / 資料を送る）のハンドラー */}
      <PrimaryActionHandler
        ref={primaryActionRef}
        onNavigateToTalkroom={() => handleTabChange("talkroom")}
      />

      {/* コメントフィールド等の置き場所が無いときの主アクション（下部バーの上） */}
      {showPrimaryActionBar && <ProjectPrimaryActionBar />}

      {/* プロジェクト全体で共通するフッター */}
      <BottomAppBarProject
        onTabChange={handleTabChange}
        activeTab={activeTab}
        isPreviewRoute={isPreviewRoute}
      />
    </ProjectWrapperStyle>
    </PrimaryActionContext.Provider>
    </ProjectShellContext.Provider>
  );
};
export default ProjectWrapper;
